import React, { useEffect, useState } from "react";
import { pullRequestPath } from "./app-routing";

type RepeatedHead = { repository: string; pr_number: number; head_sha: string; jobs_started: number };
export type ReviewOperations = {
  window: { oldest_created_at: string | null; newest_created_at: string | null; source_review_jobs: number; limit: number; truncated: boolean };
  started_review_jobs: number;
  evidence_rechecks: { jobs: number; completed: number };
  same_head_reviews: { repeated_heads: number; additional_jobs: number; unidentified_started_jobs: number; per_head: RepeatedHead[] };
  validation: { complete: number; incomplete: number; awaiting_capacity: number; unavailable: number; affected_jobs_by_reason: Record<string, number> };
  later_validated_findings_by_cited_source: Record<string, number>;
};

export function ReviewQualityReport({ data }: { data: ReviewOperations }) {
  const repeated = [...data.same_head_reviews.per_head].sort((a, b) => b.jobs_started - a.jobs_started);
  return <>
    <section className="activity-card detail-section">
      <div className="section-title"><div><span>REVIEW QUALITY</span><h2>Repeated work and validation outcomes</h2></div></div>
      <p>{data.window.source_review_jobs} source-review jobs recorded from {data.window.oldest_created_at ?? "—"} to {data.window.newest_created_at ?? "—"}.
        {data.window.truncated ? ` Showing the latest ${data.window.limit} jobs; earlier history is excluded.` : " All retained jobs in this query are included."}</p>
      <p>Uses existing review results. This page makes no model calls. Evidence rechecks are counted separately.</p>
      <dl className="review-quality-metrics">
        <div><dt>Review jobs started</dt><dd>{data.started_review_jobs}</dd></div>
        <div><dt>Heads reviewed repeatedly</dt><dd>{data.same_head_reviews.repeated_heads}</dd></div>
        <div><dt>Additional jobs on the same head</dt><dd>{data.same_head_reviews.additional_jobs}</dd></div>
        <div><dt>Incomplete validation</dt><dd>{data.validation.incomplete}</dd></div>
        <div><dt>Complete validation</dt><dd>{data.validation.complete}</dd></div>
        <div><dt>Reports unavailable</dt><dd>{data.validation.unavailable}</dd></div>
        <div><dt>Awaiting validation capacity</dt><dd>{data.validation.awaiting_capacity}</dd></div>
        <div><dt>Evidence rechecks completed</dt><dd>{data.evidence_rechecks.completed} / {data.evidence_rechecks.jobs}</dd></div>
      </dl>
      <p>Started jobs are not token or provider-call counts. A repeated head may have a different base. {data.same_head_reviews.unidentified_started_jobs} started jobs have no usable exact-head identity.</p>
    </section>
    <section className="activity-card detail-section">
      <h2>Validation failures</h2>
      {Object.keys(data.validation.affected_jobs_by_reason).length === 0 ? <p>No recorded validation failure reasons in this window.</p> :
        <div className="table-wrap"><table><thead><tr><th>Reason</th><th>Affected jobs</th></tr></thead><tbody>
          {Object.entries(data.validation.affected_jobs_by_reason).sort((a,b)=>b[1]-a[1]).map(([reason,count])=><tr key={reason}><td>{reason}</td><td>{count}</td></tr>)}
        </tbody></table></div>}
    </section>
    <section className="activity-card detail-section">
      <h2>New findings in later reviews</h2>
      <p>Newly validated findings whose cited files were unchanged are investigation leads. Changed callers or contracts can introduce an issue; these counts do not prove the first review missed a bug.</p>
      {Object.keys(data.later_validated_findings_by_cited_source).length === 0 ? <p>No eligible later-review receipts in this window.</p> :
        <div className="table-wrap"><table><thead><tr><th>Cited source</th><th>Newly validated findings</th></tr></thead><tbody>
          {Object.entries(data.later_validated_findings_by_cited_source).map(([change,count])=><tr key={change}><td>{change.replaceAll("_"," ")}</td><td>{count}</td></tr>)}
        </tbody></table></div>}
    </section>
    <section className="activity-card detail-section">
      <h2>Repeated reviews per commit</h2>
      {repeated.length === 0 ? <p>No repeated review jobs at an identified head in this window.</p> :
        <div className="table-wrap"><table><thead><tr><th>Pull request</th><th>Commit</th><th>Jobs started</th></tr></thead><tbody>
          {repeated.map(row=><tr key={`${row.repository}#${row.pr_number}:${row.head_sha}`}><td><a href={pullRequestPath(row.repository,row.pr_number)}>{row.repository} #{row.pr_number}</a></td><td><code title={row.head_sha}>{row.head_sha.slice(0,12)}</code></td><td>{row.jobs_started}</td></tr>)}
        </tbody></table></div>}
    </section>
  </>;
}

export function ReviewQualityPanel() {
  const [data,setData] = useState<ReviewOperations | null>(null);
  const [error,setError] = useState("");
  useEffect(()=>{
    const controller = new AbortController();
    async function load() {
      try {
        const response = await fetch("/api/v1/review-quality",{signal:controller.signal,cache:"no-store"});
        if (!response.ok) throw new Error(`Review quality API ${response.status}`);
        const result = await response.json();
        if (!controller.signal.aborted) { setData(result.operational); setError(""); }
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load review quality");
      }
    }
    void load();
    const timer = setInterval(()=>void load(),30000);
    return ()=>{controller.abort();clearInterval(timer);};
  },[]);
  if (error) return <section className="activity-card"><p role="alert">{error}</p></section>;
  return data ? <ReviewQualityReport data={data} /> : <section className="activity-card"><p role="status">Loading review quality…</p></section>;
}
